import type { ReactNode } from 'react';
import { Badge } from '@understory-io/leaf/badge';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { VisuallyHidden } from '@understory-io/leaf/visually-hidden';
import { css } from '@understory-io/leaf/css';
import { cx } from '~/styled-system/css';
import { Link } from 'react-router';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import { useLocaleHref } from '~/lib/use-locale-href';
import * as tracker from '~/domain/plugins/services/tracker.client';
import { buildSelectItemPayload } from '~/domain/plugins/services/tracker/events';
import { Image } from '~/ui/image';
import { LocationIcon } from '~/ui/icons/location';
import {
  formatLocationLabels,
  formatLocationsSummary,
  FormattedPrice,
} from '~/ui/formatted';
import type { ExperienceAddress } from '~/domain/experiences/index.server';
import { withExperienceFilters } from '~/lib/routing/experience-link';

/**
 * Slim view-model the home page passes into a card. Mirrors the loader's
 * narrowed shape — no `ExperienceDetails`, no `Event` blobs, only what the
 * card actually renders.
 */
export type ExperienceCardProps = {
  id: string;
  name: string;
  image: string | null;
  /**
   * Subtitle-slot content for the date row (typically the deferred "More
   * available dates" hint + its skeleton — see `ExperienceCardDates`). Rendered
   * only for non-shared cards; a shared card's owner label takes the slot
   * instead. Kept as an opaque node so the card stays free of the loader's
   * async/streaming concerns.
   */
  datesSlot?: ReactNode;
  /** Headline default price for the card. Null when the upstream price
   *  fetch failed or the default ticket has no price configured. */
  price: {
    from: number;
    fromExclusive: number;
    currency: string;
    exponent: number;
    vatExempt: boolean;
  } | null;
  /** Locations shown as a pill beneath the date row. Sourced from the
   *  next event's location when available. The card shows the city only
   *  (`appearance="default"`). */
  locations: ExperienceAddress[];
  /** Whether the price line carries the "From" prefix — the storefront's
   *  `showFromPrice` preference. Controls only the prefix; the price itself
   *  renders whenever it is available. */
  showFromPrice: boolean;
  /**
   * The owner storefront's name for a SHARED (connected) experience — used to
   * render a "by {owner}" label beneath the title. `null` for experiences
   * hosted by this storefront, which never show the label. When set, it takes
   * the subtitle slot in place of the "More available times" indicator.
   */
  ownerHostName: string | null;
  /** The home page's active filter, already rendered as the experience page's
   *  own query string (see `experienceFilterSearch`). Empty when the visitor
   *  hasn't narrowed the list. */
  filterSearch?: string;
  /**
   * Which list this card belongs to, for the `select_item` push. Defaults to
   * the home page's grid; the upcoming-events page names its own so the two
   * surfaces stay separable in GTM.
   */
  list?: { id: string; name: string };
  /**
   * Fired when the card's link is activated, alongside the built-in
   * `select_item` push — for a page that also has its own analytics event for
   * the same click. Navigation is the link's own; this is tracking only.
   */
  onLinkClick?: () => void;
  /**
   * `sizes` for the card image, describing how wide the card's grid actually
   * renders it. Defaults to the standard three-column grid — pass
   * `experienceGridImageSizes(2)` for the home page's two-card layout, which
   * renders each card considerably wider.
   */
  imageSizes?: string;
  /**
   * Fetches the card image up front at `fetchpriority="high"` instead of
   * lazily. Set on the cards of the first row: they are above the fold, any of
   * them can turn out to be the LCP element depending on viewport and image
   * dimensions, and lazy loading holds them back until after layout. Eager and
   * high-priority travel together — there is no case here where we want one
   * without the other.
   */
  imagePriority?: boolean;
  /**
   * Actions rendered at the foot of the card, above the stretched link so they
   * stay clickable — the upcoming-events page slots its "Book" button here.
   * Omitted on the home grid, where the whole card is the only affordance.
   */
  children?: ReactNode;
};

/**
 * `sizes` for a card image in the standard experience grid — one column on
 * mobile, two from `tablet`, `desktopColumns` from `desktop` — as laid out
 * inside a `<PageCard>`. Shared by the home grid and the upcoming-events grid,
 * which have the same geometry.
 *
 * Derived from the tokens the layout is actually built from: `PageCard` insets
 * by `small` (12px) on mobile and `large` (24px) from tablet, then caps its
 * content at `breakpoint-desktop` (1200px); the grid gaps by `large` (24px);
 * and the card pads by `large` (24px) on each side. So a card image is
 *
 *   mobile   100vw - 24 - 48                      = 100vw - 72px
 *   tablet   ((100vw - 48) - 24) / 2 - 48         = (100vw - 72px) / 2 - 48px
 *   desktop  (1200 - 48) / 3 - 48                 = 336px   (or 540px, two-up)
 *
 * Between 1200 and 1248 the content hasn't reached its cap yet and the real
 * image is ~16px narrower than declared. Over-declaring is the safe direction:
 * it can only pick the same candidate or the next one up, never a blurry one.
 */
export function experienceGridImageSizes(desktopColumns: 2 | 3 = 3): string {
  return [
    `(min-width: 1200px) ${desktopColumns === 2 ? '540px' : '336px'}`,
    '(min-width: 768px) calc((100vw - 72px) / 2 - 48px)',
    'calc(100vw - 72px)',
  ].join(', ');
}

/**
 * srcset candidates for a card image. Unpic's own `fullWidth` ladder starts at
 * 640 — wider than a card image ever renders on a desktop — so the low end has
 * to be supplied by hand or a correct `sizes` has nothing useful to choose
 * from. The ceiling covers the widest case (a 695px single-column card at the
 * top of the mobile range) at 2x.
 *
 * Must stay a subset of `OPTIMIZER_WIDTHS`; a test enforces it.
 */
export const CARD_IMAGE_BREAKPOINTS = [
  256, 512, 640, 750, 828, 1080, 1280, 1668,
];

/** The home grid's `select_item` list, and the default for cards that don't
 *  name one. */
const HOME_GRID_LIST = { id: 'companyProfile', name: 'Company Profile' };

/** Hoisted so the three-column default isn't rebuilt per card — the
 *  upcoming-events grid renders up to 250 of them and passes no `imageSizes`. */
const DEFAULT_IMAGE_SIZES = experienceGridImageSizes();

/**
 * A single experience tile rendered in the home page grid. Stretched-link
 * pattern: the whole card is clickable and routes to `/experience/:id`, but
 * the actual `<Link>` is overlaid behind the visible content so child Pills
 * and Text don't intercept focus or hover.
 */
export function ExperienceCard({
  id,
  name,
  image,
  datesSlot,
  price,
  locations,
  showFromPrice,
  ownerHostName,
  filterSearch = '',
  list = HOME_GRID_LIST,
  onLinkClick,
  imageSizes = DEFAULT_IMAGE_SIZES,
  imagePriority = false,
  children,
}: ExperienceCardProps) {
  const intl = useIntl();
  const localeHref = useLocaleHref();
  const href = withExperienceFilters(
    localeHref('/:locale?/experience/:experienceId', { experienceId: id }),
    filterSearch
  );
  const priceValue = price !== null && (
    <Text as="span" style="body-large-bold" color="primary">
      <FormattedPrice
        price={{
          vatInclusivePriceCents: price.from,
          vatExclusivePriceCents: price.fromExclusive,
          currency: price.currency,
          exponent: price.exponent,
          vatExempt: price.vatExempt,
        }}
      />
    </Text>
  );

  return (
    <article
      className={cx(cardStyles, children ? cardWithActionsStyles : undefined)}
    >
      <Link
        to={href}
        className={stretchedLinkStyles}
        // An experience picked from a grid — the funnel's `select_item`
        // moment (US-11215). The default list id/name match the legacy
        // storefront's push, which the GTM container was built against.
        onClick={() => {
          tracker.event(
            'select_item',
            buildSelectItemPayload({
              experienceId: id,
              experienceName: name,
              listId: list.id,
              listName: list.name,
            })
          );
          onLinkClick?.();
        }}
      >
        <VisuallyHidden>
          <FormattedMessage
            defaultMessage="See {experienceName}"
            description="Visually-hidden accessible label for an experience card link on the home page"
            values={{ experienceName: name }}
          />
        </VisuallyHidden>
      </Link>

      <div className={imageWrapStyles}>
        {image ? (
          <Image
            src={image}
            alt=""
            layout="fullWidth"
            sizes={imageSizes}
            breakpoints={CARD_IMAGE_BREAKPOINTS}
            // unpic's `priority` is exactly this pairing: loading="eager" plus
            // fetchpriority="high".
            priority={imagePriority}
            className={imageStyles}
            placeholder={
              <span
                className={css({
                  display: 'block',
                  background: 'surface.background',
                  width: 'full',
                  height: 'full',
                })}
              ></span>
            }
          />
        ) : (
          <span
            className={css({
              display: 'block',
              background: 'surface.background',
              width: 'full',
              height: 'full',
            })}
          ></span>
        )}
      </div>

      <Stack gap="small" className={contentStackStyles}>
        <Stack gap="micro">
          <Text as="h3" style="subheadline-small" className={nameStyles}>
            {name}
          </Text>
          {/* One subtitle line. Shared experiences show the owner attribution;
           *  otherwise the date-row slot (the "More available times" hint) takes
           *  the same slot. Both share the tertiary small style so the row is
           *  visually stable. */}
          {ownerHostName ? (
            <Text as="span" style="body-small-regular" color="tertiary">
              <FormattedMessage
                defaultMessage="by {hostName}"
                description="Experience host name for a shared Connect experience"
                values={{ hostName: ownerHostName }}
              />
            </Text>
          ) : (
            (datesSlot ?? null)
          )}
        </Stack>

        <div className={metaRowStyles}>
          {locations.length > 0 && (
            <span className={pillWrapStyles}>
              <Badge appearance="neutral" icon={<LocationIcon />}>
                {formatLocations(intl, locations)}
              </Badge>
            </span>
          )}
          {priceValue && (
            <Text
              as="p"
              style="body-large-bold"
              color="tertiary"
              className={priceStyles}
            >
              {showFromPrice ? (
                <FormattedMessage
                  defaultMessage="From {price}"
                  description="'From X kr.' price label on an experience card on the home page"
                  values={{ price: priceValue }}
                />
              ) : (
                priceValue
              )}
            </Text>
          )}
        </div>

        {/* Above the stretched link, so the action is clickable rather than
            being swallowed by the card-wide anchor behind it. */}
        {children && <div className={actionsStyles}>{children}</div>}
      </Stack>
    </article>
  );
}

/**
 * The card's location line follows the same summary format as the experience
 * page's pill (US-10985): distinct place labels "or"-joined, first two plus
 * "N more" past three. The count is distinct places, not raw addresses —
 * several addresses in one city read as a single place.
 */
function formatLocations(
  intl: IntlShape,
  locations: ExperienceAddress[]
): string {
  return formatLocationsSummary(intl, formatLocationLabels(locations));
}

const cardStyles = css({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  gap: 'large',
  background: 'surface.raised',
  // 32px corners == the `xlarge` radius token (matches the booking panel).
  borderRadius: 'xlarge',
  shadow: 'large',
  // Fixed card height per design. `overflow: hidden` clips any content that
  // would exceed it (and the title is line-clamped below), so cards stay a
  // uniform 400px regardless of name length or which subtitle line renders.
  overflow: 'hidden',
  height: '[400px]',
  // Fill the slot the layout gives the card, whatever that layout is. The home
  // grid's <li> is a block, where a flex container fills the line box anyway —
  // but the upcoming-events grid's <li> is itself a flex row, and there an
  // auto-width item sizes to its CONTENT, so cards came out as wide as their
  // longest line rather than as wide as their column.
  width: 'full',
  // Uniform padding around the whole card; the image is an inset child (with
  // its own radius) rather than a full-bleed header.
  padding: 'large',
});

// Actions are extra content the 400px design never budgeted for, and
// `overflow: hidden` would simply clip them. Cards carrying them grow instead,
// keeping 400px as the floor so a grid mixing both still looks even.
const cardWithActionsStyles = css({
  height: 'auto',
  minHeight: '[400px]',
});

// Clamp long experience names to two lines so a verbose title can't push the
// date/meta rows out of the fixed-height card.
const nameStyles = css({
  lineClamp: '2',
});

const stretchedLinkStyles = css({
  position: 'absolute',
  inset: '[0]',
  zIndex: '[1]',
  borderRadius: 'xlarge',
  _focusVisible: {
    outline: 'medium',
    outlineColor: 'border.focus',
    outlineOffset: 'tiny',
  },
});

const imageWrapStyles = css({
  width: 'full',
  // Fixed card-image height per design (no token maps to 212px). Rendered even
  // when `image` is null so the card preserves the visual rhythm of the grid.
  height: '[212px]',
  // 16px corners == the `medium` radius token.
  borderRadius: 'medium',
  overflow: 'hidden',
});

const imageStyles = css({
  width: 'full',
  height: 'full',
  objectFit: 'cover',
});

const contentStackStyles = cx(
  css({
    // Horizontal/bottom insets now come from the card's own padding; the
    // content just fills the remaining height.
    // Re-elevate above the stretched link so anchored content (the location
    // badge, etc.) doesn't steal pointer events. The badge is non-interactive
    // on the card, but this keeps the structure consistent with UpcomingDates.
    position: 'relative',
    zIndex: '[2]',
    pointerEvents: 'none',
    flex: '1',
  })
);

const metaRowStyles = css({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'small',
  flexWrap: 'wrap',
  marginTop: 'auto',
});

// The card is a stretched-link container — every visible child must let
// clicks pass through to the underlying <Link>. The location badge is
// non-interactive on the card, so `pointer-events: none` matches the price
// text's behavior inside `contentStackStyles`.
const pillWrapStyles = css({
  pointerEvents: 'none',
});

const priceStyles = css({
  textAlign: 'right',
  marginLeft: 'auto',
});

// Sits above the card-wide stretched link so the slotted action actually
// receives its clicks, and stretches so a lone button fills the card's width.
// `pointerEvents` is re-enabled because the surrounding content stack turns it
// off — everything else in the card is text that should fall through to the
// link, but an action is the one thing that must not.
const actionsStyles = css({
  position: 'relative',
  zIndex: '[2]',
  display: 'flex',
  flexDirection: 'column',
  pointerEvents: 'auto',
  marginTop: 'medium',
});
